import Chart from "chart.js/auto";
import { memo, useEffect, useLayoutEffect, useRef } from "react";
import {
  RADAR_COLOR_ARRAY,
  RADAR_LABEL_ARRAY,
  BASE_RADAR_CHART_DATASET,
  GENERAL_POPULATION_RADAR_CHART_DATASET,
} from "./constants";

interface Props {
  canvasId: string;
  size?: "small" | "medium" | "large";
  dataSets?: {
    data: number[];
    fill?: boolean;
    backgroundColor?: string;
    borderColor?: string;
    pointBackgroundColor?: string;
    pointBorderColor?: string;
    pointHoverBackgroundColor?: string;
    pointHoverBorderColor?: string;
    label?: string;
  }[];
  includeGeneralPopulation?: boolean;
  includeDashedBaseline?: boolean;
  hideCornerLabels?: boolean;
  hideLegend?: boolean;
}

const RadarChart = ({
  canvasId,
  size = "small",
  dataSets,
  includeGeneralPopulation,
  includeDashedBaseline,
  hideCornerLabels,
  hideLegend,
}: Props) => {
  const chartRefInstance = useRef<{ chart: Chart | null }>({ chart: null });
  const chartRef = useRef<HTMLCanvasElement>(null);

  // add a useEffect to redraw the chart when the window is resized
  useEffect(() => {
    const onResize = () => {
      if (chartRefInstance.current.chart) {
        chartRefInstance.current.chart.resize();
      }
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  useLayoutEffect(() => {
    if (!chartRef?.current) {
      return;
    }
    const datasets = dataSets ? [...dataSets] : [];

    if (includeGeneralPopulation) {
      datasets.push(GENERAL_POPULATION_RADAR_CHART_DATASET);
    }

    if (includeDashedBaseline) {
      datasets.push(BASE_RADAR_CHART_DATASET);
    }

    chartRefInstance.current.chart = new Chart(chartRef.current, {
      type: "radar",
      data: {
        labels: [...RADAR_LABEL_ARRAY],
        datasets,
      },

      options: {
        // Disabled so a chart recreated mid-flow (e.g. re-rendered right
        // before a PDF/html2canvas capture) is drawn fully on the first
        // frame instead of being snapshotted mid opening-animation.
        animation: false,
        plugins: {
          legend: {
            labels: {
              filter: (legendItem) =>
                hideLegend ? false : legendItem.text !== "",
            },
            position: "bottom",
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const rawNumber = context.raw ? Number(context.raw) - 25 : 0;
                return `${context.dataset.label}: ${rawNumber}`;
              },
            },
          },
        },
        scales: {
          r: {
            min: 0,
            max: 125,
            beginAtZero: true,
            pointLabels: {
              color: ({ index }) => RADAR_COLOR_ARRAY[index],
              font: {
                size: 14,
                weight: "bold",
              },
            },
            ticks: {
              stepSize: 25,
              display: false,
            },
          },
        },
      },
    });

    const chart = chartRefInstance.current.chart;
    return () => {
      if (chart) {
        chart.destroy();
      }
    };
  }, [
    size,
    dataSets,
    includeDashedBaseline,
    includeGeneralPopulation,
    hideLegend,
  ]);

  return (
    <div className={`radar-chart-wrapper ${size}`}>
      {hideCornerLabels ? null : (
        <div className="radar-chart-corner-labels">
          <div className="cyan label">
            <span>Attitudes & Outlook</span>
          </div>
          <div className="sapphire label">
            <span>Work Habits</span>
          </div>
        </div>
      )}
      <div
        style={{
          width: "100%",
          maxWidth: "600px",
          alignSelf: "center",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <canvas id={canvasId} ref={chartRef} />
      </div>
      {hideCornerLabels ? null : (
        <div className="radar-chart-corner-labels">
          <div className="poppy label">
            <span>Temperament</span>
          </div>
          <div className="gold label">
            <span>Interaction Style</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(RadarChart);
